import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ManualeButton } from "@/components/ManualeButton";
import AbiShell from "@/pages/abi/AbiShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Search, Loader2, Building2, Trash2, ArrowRight, Users, RefreshCw, FolderOpen, Plus } from "lucide-react";

const TIPO_LABEL = { piva: "P.IVA", cf: "C.F.", nome: "Ragione sociale" };

export default function AbiDashboard() {
  const nav = useNavigate();
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("assicurativa");
  const [modes, setModes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [q, setQ] = useState("");
  const [dup, setDup] = useState(null);

  useEffect(() => {
    api.get("/abi/meta").then(({ data }) => { setModes(data.modes); setMode(data.default_mode); }).catch(() => {});
  }, []);

  const loadHistory = () => {
    api.get("/abi/analyses", { params: q ? { q } : {} }).then(({ data }) => setHistory(data)).catch(() => {});
  };
  useEffect(() => { loadHistory(); /* eslint-disable-next-line */ }, [q]);

  const analyze = async () => {
    if (!query.trim()) { toast.error("Inserisci P.IVA, C.F. o Ragione Sociale"); return; }
    setBusy(true);
    try {
      const { data: ex } = await api.get("/abi/analyses-esistente", { params: { query: query.trim() } });
      if (ex.esiste) { setDup(ex.analisi); return; }
      await creaNuova();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const creaNuova = async () => {
    setDup(null);
    setBusy(true);
    try {
      const { data } = await api.post("/abi/analyze", { query: query.trim(), mode });
      toast.success("Dossier creato");
      nav(`/business-intelligence/analisi/${data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Eliminare questa analisi?")) return;
    try { await api.delete(`/abi/analyses/${id}`); loadHistory(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <AbiShell>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="abi-dashboard">
        {/* Hero ricerca */}
        <div className="rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-white p-8 mb-8">
          <p className="text-xs uppercase tracking-[0.2em] text-violet-500 mb-2">Assietica Business Intelligence</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-slate-900 mb-2">Analizza un'azienda</h1>
          <p className="text-slate-500 mb-6 max-w-2xl">Inserisci un solo dato: ricerca multifonte, verifica, incrocio e pre-analisi assicurativa su informazioni pubblicamente disponibili.</p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-3xl">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && analyze()}
                placeholder="Inserisci Partita IVA, Codice Fiscale o Ragione Sociale"
                className="pl-11 h-12 bg-white text-base" data-testid="abi-search-input" />
            </div>
            <Button onClick={analyze} disabled={busy} className="h-12 px-6 bg-violet-600 hover:bg-violet-700 text-white" data-testid="abi-analyze-btn">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Analizza azienda <ArrowRight className="h-4 w-4 ml-1.5" /></>}
            </Button>
          </div>
          <div className="flex items-center gap-2 mt-4">
            <span className="text-xs text-slate-500">Modalità:</span>
            {modes.map((m) => (
              <button key={m.id} onClick={() => setMode(m.id)} title={m.descr} data-testid={`abi-mode-${m.id}`}
                className={`px-3 py-1 rounded-full text-xs border transition-colors ${mode === m.id ? "border-violet-500 bg-violet-100 text-violet-700 font-semibold" : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"}`}>
                {m.label}
              </button>
            ))}
          </div>
          <button onClick={() => nav("/business-intelligence/terzi")} data-testid="abi-terzi-link"
            className="mt-4 inline-flex items-center gap-1.5 text-xs text-violet-600 hover:text-violet-800 hover:underline">
            <Users className="h-3.5 w-3.5" /> Gestione accessi Terzi (portale esterno)
          </button>
          <div className="mt-4 ml-4 inline-block"><ManualeButton route="/manuale/business-intelligence" /></div>
        </div>

        <Dialog open={!!dup} onOpenChange={(o) => !o && setDup(null)}>
          <DialogContent data-testid="abi-dup-dialog">
            <DialogHeader>
              <DialogTitle>Analisi già esistente</DialogTitle>
              <DialogDescription>
                <b>{dup?.denominazione || dup?.identificativo || dup?.query}</b> è già stata analizzata
                {dup?.updated_at ? ` (ultimo aggiornamento ${new Date(dup.updated_at).toLocaleDateString("it-IT")})` : ""}.
                Puoi riaprirla, aggiornarne i dati (con confronto rispetto alla versione precedente) oppure crearne una nuova.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={creaNuova} data-testid="abi-dup-nuova"><Plus className="h-4 w-4 mr-1.5" /> Crea nuova comunque</Button>
              <Button variant="outline" onClick={() => nav(`/business-intelligence/analisi/${dup.id}?aggiorna=1`)} data-testid="abi-dup-aggiorna"><RefreshCw className="h-4 w-4 mr-1.5" /> Aggiorna dati</Button>
              <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={() => nav(`/business-intelligence/analisi/${dup.id}`)} data-testid="abi-dup-apri"><FolderOpen className="h-4 w-4 mr-1.5" /> Apri esistente</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cronologia */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-semibold text-slate-800">Aziende analizzate</h2>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nella cronologia…" className="pl-9 bg-white h-9" data-testid="abi-history-search" />
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Soggetto</TableHead><TableHead>Tipo</TableHead><TableHead>Modalità</TableHead>
              <TableHead>Stato</TableHead><TableHead>Completezza</TableHead><TableHead>Data</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {history.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">Nessuna analisi. Inserisci una P.IVA per iniziare.</TableCell></TableRow>
              ) : history.map((h) => (
                <TableRow key={h.id} className="cursor-pointer hover:bg-slate-50" onClick={() => nav(`/business-intelligence/analisi/${h.id}`)} data-testid={`abi-history-row-${h.id}`}>
                  <TableCell className="font-medium text-slate-800">
                    <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-violet-500" />{h.denominazione || h.identificativo || h.query}</div>
                  </TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{TIPO_LABEL[h.tipo_input] || h.tipo_input}</Badge></TableCell>
                  <TableCell className="text-xs text-slate-500 capitalize">{h.mode}</TableCell>
                  <TableCell><Badge className={`text-xs ${h.stato === "completato" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{h.stato}</Badge></TableCell>
                  <TableCell className="text-xs">{h.completezza?.totale ?? 0}/100</TableCell>
                  <TableCell className="text-xs text-slate-500 whitespace-nowrap">{(h.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell><Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={(e) => del(h.id, e)} data-testid={`abi-history-del-${h.id}`}><Trash2 className="h-3.5 w-3.5" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>
    </AbiShell>
  );
}
